import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Checkbox, Form, Input, Modal, Select, Typography, message } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import fetchAPI from '../../../lib/utils/fetch-api'
import { flattenOrganizations } from './company/shared'
import type { CompanyStructure, RoleItem } from './company/types'
import { officeOptionKey } from '../../lib/import-upload'
import type { OfficeSelectOption } from '../../lib/import-upload'

const { Paragraph } = Typography

/** Активные офисы активных организаций — только в них можно приглашать. */
export function flattenOfficeOptionsForInvite(structure: CompanyStructure): OfficeSelectOption[] {
  const options: OfficeSelectOption[] = []
  for (const org of flattenOrganizations(structure)) {
    if (!org.is_active) continue
    for (const office of org.offices) {
      if (!office.is_active) continue
      options.push({
        officeId: office.id,
        organizationId: org.id,
        label: `${org.name} → ${office.name}`,
      })
    }
  }
  return options
}

type Props = {
  open: boolean
  onClose: () => void
  officeOptions: OfficeSelectOption[]
  inviteTtlHours?: number | null
  /** Вызывается после успешной отправки приглашения */
  onInvited?: () => void
}

export function InviteEmployeeModal({
  open,
  onClose,
  officeOptions,
  inviteTtlHours,
  onInvited,
}: Props) {
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [roles, setRoles] = useState<RoleItem[]>([])
  const [loading, setLoading] = useState(false)

  const selectOptions = useMemo(
    () => officeOptions.map(opt => ({ value: officeOptionKey(opt), label: opt.label })),
    [officeOptions],
  )

  const loadRoles = useCallback(async () => {
    try {
      const res = await fetchAPI('/api/roles/list')
      const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : []
      setRoles(
        list
          .filter((r: any) => r?.is_active !== false)
          .map((r: any) => ({ id: Number(r.id), name: String(r.name || '') })),
      )
    } catch {
      /* non-blocking */
    }
  }, [])

  useEffect(() => {
    if (!open) return
    loadRoles()
    form.resetFields()
    if (officeOptions.length) form.setFieldsValue({ office: officeOptionKey(officeOptions[0]) })
  }, [open, officeOptions, form, loadRoles])

  const submit = async (values: any) => {
    const [organizationId, officeId] = String(values.office || '')
      .split(':')
      .map(Number)
    if (!organizationId || !officeId) {
      message.error('Выберите офис')
      return
    }
    setLoading(true)
    try {
      const res = await fetchAPI('/api/home/employees/invite', {
        method: 'POST',
        body: JSON.stringify({
          email: String(values.email || '').trim(),
          organization_id: organizationId,
          office_id: officeId,
          role_ids: values.role_ids || [],
        }),
      })
      if (res?.errors) {
        message.error(res.errors?.[0]?.detail || 'Не удалось отправить приглашение')
        return
      }
      message.success(res?.data?.message || 'Приглашение отправлено')
      onClose()
      onInvited?.()
    } catch (e: any) {
      message.error(e?.message || 'Ошибка отправки')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      title="Пригласить сотрудника по e-mail"
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={loading}
      okText="Отправить приглашение"
      cancelText="Отмена"
      width={520}
      destroyOnClose
    >
      <Paragraph type="secondary">
        Сотрудник получит письмо со ссылкой, где сам задаст пароль
        {inviteTtlHours ? `. Ссылка действует ${inviteTtlHours} ч.` : '.'}
      </Paragraph>
      <Form form={form} layout="vertical" onFinish={submit}>
        {officeOptions.length > 1 && (
          <Form.Item name="office" label="Офис" rules={[{ required: true, message: 'Выберите офис' }]}>
            <Select options={selectOptions} placeholder="Выберите офис" />
          </Form.Item>
        )}
        {officeOptions.length === 1 && (
          <Form.Item name="office" hidden>
            <Input />
          </Form.Item>
        )}
        <Form.Item
          name="email"
          label="E-mail"
          rules={[
            { required: true, message: 'Укажите e-mail' },
            { type: 'email', message: 'Введите корректный e-mail' },
          ]}
        >
          <Input placeholder="employee@example.ru" autoComplete="off" />
        </Form.Item>
        <Form.Item
          name="role_ids"
          label="Роли"
          rules={[
            {
              validator: (_, value) =>
                value && value.length
                  ? Promise.resolve()
                  : Promise.reject(new Error('Выберите хотя бы одну роль')),
            },
          ]}
        >
          <Checkbox.Group
            style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}
            options={roles.map(r => ({ label: r.name, value: r.id }))}
          />
        </Form.Item>
        <Button
          type="link"
          icon={<PlusOutlined />}
          style={{ padding: 0 }}
          onClick={() => navigate('/admin/roles')}
        >
          Управление ролями
        </Button>
      </Form>
    </Modal>
  )
}

export default InviteEmployeeModal